import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import PaymentReceipt from '../components/payments/PaymentReceipt';
import { getAppNavItems } from '../utils/appNavItems';
import { getPaymentById } from '../utils/payments';
import { formatUsd } from '../data/studyPlans';
import { SBA } from '../theme';

export default function PaymentConfirmationPage() {
  const { paymentId } = useParams();
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const navItems = getAppNavItems(isAdmin, '/payments', { hasSubscription });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const row = await getPaymentById(paymentId);
        if (cancelled) return;
        if (!row) {
          setError('Payment record not found.');
          setPayment(null);
        } else if (!isAdmin && row.userId !== currentUser?.uid) {
          setError('You do not have access to this receipt.');
          setPayment(null);
        } else {
          setPayment(row);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load payment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paymentId, currentUser?.uid, isAdmin]);

  return (
    <AppShell navItems={navItems}>
      <div style={s.wrap}>
        <div style={s.hero}>
          <div style={s.check}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, color: '#15803d' }}>
              check_circle
            </span>
          </div>
          <h1 style={s.title}>Payment confirmed</h1>
          <p style={s.sub}>
            Your payment was recorded. This page shows the order reference and amount only.
          </p>
          {payment ? (
            <p style={s.refBanner}>
              Reference: <strong>{payment.referenceNumber}</strong> · {formatUsd(payment.amount)}
            </p>
          ) : null}
        </div>

        {loading ? <p>Loading receipt…</p> : null}
        {error ? <div style={s.error}>{error}</div> : null}

        {payment ? <PaymentReceipt payment={payment} /> : null}

        <div style={s.links} className="no-print">
          <Link to="/payments" style={s.linkPrimary}>
            View all payments
          </Link>
          <Link to="/dashboard" style={s.linkGhost}>
            Go to dashboard
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

const s = {
  wrap: { maxWidth: 780, margin: '0 auto', padding: '24px 18px 48px' },
  hero: { textAlign: 'center', marginBottom: 24 },
  check: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#ecfdf5',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  title: { margin: '0 0 8px', fontSize: 28, fontWeight: 800, color: SBA.navy },
  sub: { margin: 0, color: '#475569', fontSize: 14, lineHeight: 1.55 },
  refBanner: {
    margin: '14px auto 0',
    display: 'inline-block',
    padding: '8px 14px',
    borderRadius: 999,
    background: 'rgba(197,160,70,0.15)',
    color: SBA.navy,
    fontSize: 13,
  },
  error: {
    padding: '12px 14px',
    borderRadius: 12,
    background: '#fef2f2',
    color: '#b91c1c',
    fontWeight: 600,
    marginBottom: 16,
  },
  links: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 24,
  },
  linkPrimary: {
    padding: '12px 18px',
    borderRadius: 12,
    background: SBA.navy,
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 700,
    fontSize: 14,
  },
  linkGhost: {
    padding: '12px 18px',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    color: SBA.navy,
    textDecoration: 'none',
    fontWeight: 700,
    fontSize: 14,
    background: '#fff',
  },
};
